import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useContext,
  createContext,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const SelectContext = createContext(null);

function Select({ value, onValueChange, defaultValue, children, disabled, name }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(() => new Map());
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const [highlightedValue, setHighlightedValue] = useState(null);
  const [typedChars, setTypedChars] = useState('');
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const typedTimeoutRef = useRef(null);

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleChange = useCallback(
    (newValue) => {
      if (!isControlled) setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  const selectItem = useCallback(
    (itemValue) => {
      handleChange(itemValue);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [handleChange]
  );

  const registerItem = useCallback((itemValue, label) => {
    setItems((prev) => {
      if (prev.get(itemValue) === label) return prev;
      const next = new Map(prev);
      next.set(itemValue, label);
      return next;
    });
  }, []);

  const unregisterItem = useCallback((itemValue) => {
    setItems((prev) => {
      if (!prev.has(itemValue)) return prev;
      const next = new Map(prev);
      next.delete(itemValue);
      return next;
    });
  }, []);

  useEffect(() => {
    if (open) {
      const itemKeys = Array.from(items.keys());
      if (currentValue && items.has(currentValue)) {
        setHighlightedValue(currentValue);
      } else if (itemKeys.length > 0) {
        setHighlightedValue(itemKeys[0]);
      }
    } else {
      setHighlightedValue(null);
      setTypedChars('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event) {
      const triggerEl = triggerRef.current;
      const contentEl = contentRef.current;
      const clickedInsideTrigger = triggerEl && triggerEl.contains(event.target);
      const clickedInsideContent = contentEl && contentEl.contains(event.target);
      if (!clickedInsideTrigger && !clickedInsideContent) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event) {
      const itemKeys = Array.from(items.keys());
      if (itemKeys.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const currentIndex = itemKeys.indexOf(highlightedValue);
        const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % itemKeys.length;
        setHighlightedValue(itemKeys[nextIndex]);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const currentIndex = itemKeys.indexOf(highlightedValue);
        const prevIndex = currentIndex <= 0 ? itemKeys.length - 1 : currentIndex - 1;
        setHighlightedValue(itemKeys[prevIndex]);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setHighlightedValue(itemKeys[0]);
      } else if (event.key === 'End') {
        event.preventDefault();
        setHighlightedValue(itemKeys[itemKeys.length - 1]);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (highlightedValue !== null) {
          selectItem(highlightedValue);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (event.key === 'Tab') {
        setOpen(false);
      } else if (event.key.length === 1 && /\S/.test(event.key)) {
        const newTyped = (typedChars + event.key).toLowerCase();
        setTypedChars(newTyped);
        const match = Array.from(items.entries()).find(([, label]) => {
          const labelStr = String(label).toLowerCase();
          return labelStr.startsWith(newTyped);
        });
        if (match) {
          setHighlightedValue(match[0]);
        }
        clearTimeout(typedTimeoutRef.current);
        typedTimeoutRef.current = setTimeout(() => setTypedChars(''), 750);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(typedTimeoutRef.current);
    };
  }, [open, highlightedValue, items, selectItem, typedChars]);

  const contextValue = {
    open,
    setOpen,
    value: currentValue,
    items,
    selectItem,
    registerItem,
    unregisterItem,
    disabled,
    highlightedValue,
    setHighlightedValue,
    triggerRef,
    contentRef,
  };

  return (
    <SelectContext.Provider value={contextValue}>
      <div className="relative">
        {children}
      </div>
      {name !== undefined && (
        <input type="hidden" name={name} value={currentValue} readOnly />
      )}
    </SelectContext.Provider>
  );
}

function SelectGroup({ children, className }) {
  return (
    <div role="group" className={className}>
      {children}
    </div>
  );
}

function SelectValue({ placeholder, className }) {
  const { value, items } = useContext(SelectContext);

  if (value === undefined || value === null || value === '') {
    return <span className={cn('text-slate-500', className)}>{placeholder}</span>;
  }

  return <span className={className}>{items.get(value) ?? value}</span>;
}

function SelectTrigger({ className, children, id, 'aria-label': ariaLabel, ...props }) {
  const { open, setOpen, disabled, triggerRef } = useContext(SelectContext);

  function handleKeyDown(event) {
    if (open) return;
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      setOpen(true);
    }
  }

  function handleFocus(event) {
    if (open) return;
    if (event.target.matches(':focus-visible')) {
      setOpen(true);
    }
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      id={id}
      disabled={disabled}
      onClick={() => !disabled && setOpen((o) => !o)}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      data-state={open ? 'open' : 'closed'}
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors',
        'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={cn('size-4 shrink-0 opacity-50 transition-transform', open && 'rotate-180')}
        aria-hidden="true"
      />
    </button>
  );
}

function SelectContent({ className, children }) {
  const { open, triggerRef, contentRef } = useContext(SelectContext);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPosition(null);
      return;
    }

    function updatePosition() {
      const triggerEl = triggerRef.current;
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - 16;
      const spaceAbove = rect.top - 16;
      const desiredMaxHeight = 480;
      const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;
      const availableSpace = openUpward ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(120, Math.min(desiredMaxHeight, availableSpace));
      const top = openUpward ? rect.top - maxHeight - 8 : rect.bottom + 8;

      setPosition({
        top: Math.max(8, top),
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    }

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, triggerRef]);

  if (!open || !position) return null;

  return createPortal(
    <div
      ref={contentRef}
      role="listbox"
      data-state="open"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        maxHeight: `${position.maxHeight}px`,
      }}
      className={cn(
        'z-50 overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg',
        className
      )}
    >
      {children}
    </div>,
    document.body
  );
}

function SelectItem({ value, children, className, disabled }) {
  const {
    selectItem,
    value: currentValue,
    registerItem,
    unregisterItem,
    highlightedValue,
    setHighlightedValue,
  } = useContext(SelectContext);
  const itemRef = useRef(null);

  useLayoutEffect(() => {
    registerItem(value, children);
    return () => unregisterItem(value);
  }, [value, children, registerItem, unregisterItem]);

  const isSelected = currentValue === value;
  const isHighlighted = highlightedValue === value;

  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isHighlighted]);

  return (
    <button
      ref={itemRef}
      type="button"
      role="option"
      tabIndex={-1}
      aria-selected={isSelected}
      disabled={disabled}
      onClick={() => !disabled && selectItem(value)}
      onMouseEnter={() => !disabled && setHighlightedValue(value)}
      className={cn(
        'relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors',
        isHighlighted && !isSelected && 'bg-slate-100',
        isSelected && 'bg-brand-50 font-semibold text-brand-700',
        isHighlighted && isSelected && 'bg-brand-100',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <span className="flex-1">{children}</span>
      {isSelected && <Check className="size-4 shrink-0" aria-hidden="true" />}
    </button>
  );
}

function SelectLabel({ className, children }) {
  return (
    <div className={cn('px-3 py-1.5 text-sm font-semibold text-slate-700', className)}>
      {children}
    </div>
  );
}

function SelectSeparator({ className }) {
  return <div className={cn('-mx-1 my-1 h-px bg-slate-200', className)} />;
}

const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};